import { Argon2BrowserHashResult } from "argon2-browser"
import { defineStore } from "pinia"
import { generateCodeChallenge, generateCodeVerifier } from "src/auth/pkce"
import {
  refreshAccessToken,
  registerDevice,
  requestAccessToken,
  requestAuthorizationCode,
  revokeToken,
  verifyAccessToken,
} from "src/auth/token"
import { AuthenticationToken, ClientCredentials } from "src/auth/types"
import { csiAPI } from "src/boot/axios"
import { syncDBCache, syncUnsubmittedWork } from "src/boot/databaseCacheSync"
import { LINKS } from "src/env/links"
import router from "src/router"
import { useApiCallStore } from "src/stores/apiCallStore"
import { useConfigurationStore } from "src/stores/configurationStore"
import { Log } from "src/util/classes/log"
import { NamedManagerPath, NamedPath } from "src/util/enums/namedPaths"
import { authenticationErrorDialog, dataFetchDialog } from "src/util/functions/dialogs"
import { clearLocalStorage, resetPiniaStores } from "src/util/functions/reset"
import { WorkspaceDetail } from "src/util/types/dataModels/workspace"

export interface AuthStoreType {
  email: string
  passHashed: Argon2BrowserHashResult | null
  unlockKeyHashed: Argon2BrowserHashResult | null
  isLocked: boolean
  selectedWorkspace: WorkspaceDetail | null
  clientCredentials: ClientCredentials | null
  authenticationToken: AuthenticationToken | null
  sso?: boolean
  codeCheck: PKCE | Record<string, never> // Allows for empty object assignment
  authorizationCode: string | null
}

export interface PKCE {
  codeVerifier?: string
  codeChallenge?: string
}

//TODO: Add comments on the functionality of methods
export const useAuthStore = defineStore("pinia-authStore", {
  persist: true,

  state: (): AuthStoreType => ({
    email: "",
    passHashed: null,
    unlockKeyHashed: null,
    isLocked: false,
    selectedWorkspace: null,
    clientCredentials: null,
    authenticationToken: null,
    sso: false,
    codeCheck: {},
    authorizationCode: null,
  }),

  getters: {
    /**
     * If we detect that there is a valid authentication token object (access token + rest),
     * then we know that the user is logged in, as that token is required for all HTTP operations.
     *
     * @param state
     */
    isLoggedIn(state: AuthStoreType): boolean {
      return Boolean(state.authenticationToken)
    },

    /**
     * Returns the access token type and the access token for the logged-in user
     * ex: "Bearer FtAgiIh+RGTojcgme7JKCwgJQVGDDCyE145SitHtdikxYAF"
     *
     * In case the access token is not available, returns an empty string
     *
     * @param state
     */
    getAccessTokenString(state: AuthStoreType): string {
      if (!state.authenticationToken) return ""
      return state.authenticationToken.token_type + " " + state.authenticationToken.access_token
    },

    /**
     * Checks if all required data needed for requesting access tokens is present.
     *
     * @param state
     */
    canRequestAccessToken(state: AuthStoreType): boolean {
      // The order of checks is the order we do in the login sequence
      return (
        Boolean(state.clientCredentials) &&
        Boolean(state.codeCheck?.codeVerifier) &&
        Boolean(state.authorizationCode)
      )
    },

    getCSIBaseUrl(state: AuthStoreType): string {
      const uuid = state.selectedWorkspace?.id || ""
      return LINKS.CSI_URL + "/" + uuid
    },
  },

  actions: {
    lock() {
      this.isLocked = true
    },

    unlock() {
      this.isLocked = false
    },

    /**
     * Sets the selected workspace to the one provided.
     * The selected workspace is used for all internal workspace related actions.
     * This also changes the CSI HTTP baseURL, fetches all the data if this is the first time we have connected to
     * this workspace, and also it rebuilds the cache and DB event subscriptions.
     *
     * If the data fetch is aborted, then we reset the selected workspace.
     *
     * @param workspace
     */
    async setSelectedWorkspace(workspace: WorkspaceDetail) {
      this.selectedWorkspace = workspace

      // Change base URL for the CSI, so that HTTP requests would go to the correct endpoints
      csiAPI.defaults.baseURL = this.getCSIBaseUrl

      const apiCallStore = useApiCallStore()
      const configurationStore = useConfigurationStore()

      // If there is no record, this means that it is the first time we enter the workspace, so we fetch all data
      if (
        configurationStore.downloadDataOnStartUp &&
        !apiCallStore.workspaceLastCall[workspace.id]
      ) {
        const dataFetched = await dataFetchDialog(workspace.id)
        if (!dataFetched) {
          const routeName = process.env.IS_MANAGER_MODE
            ? NamedManagerPath.SELECT_WORKSPACE
            : NamedPath.SELECT_WORKSPACE

          // Fetch aborted, so we reset the values
          this.selectedWorkspace = null
          csiAPI.defaults.baseURL = undefined
          delete apiCallStore.workspaceLastCall[workspace.id]

          return router.push({ name: routeName })
        }
      }

      // We switch to a different workspace, so rebuild cache and resubscribe to DB events
      await syncDBCache()
      await syncUnsubmittedWork()
    },

    setNewPasswordHashed(password: Argon2BrowserHashResult) {
      this.passHashed = password
    },

    setUnlockKeyHashed(key: Argon2BrowserHashResult) {
      this.unlockKeyHashed = key
    },

    /**
     * Resets the auth store data and attempts to clean all extraneous data.
     * Carries over some data, like the client credentials, since they are device specific and not user specific.
     * Routes the user back to the app initiation screen.
     */
    async logout() {
      // Transfer over client credentials since they are device specific
      const savedClientCredentials = this.clientCredentials

      try {
        await this.revokeToken()
      } catch (error) {
        Log.warning({ message: "Failed to revoke token", error })
      }

      resetPiniaStores()
      clearLocalStorage()

      // NOTE: In some cases you might need to manually delete this if your local IP changes.
      this.clientCredentials = savedClientCredentials
      localStorage.removeItem("mockWorkspaceData")

      await router.push({ name: NamedPath.INITIATE_APP })
    },

    /**
     * Attempts to register the device on springboard.
     *
     * @throws Failed HTTP request
     */
    async registerDevice() {
      try {
        this.clientCredentials = await registerDevice()
      } catch (error) {
        Log.debug("Could not register the device.")
        throw error
      }
    },

    /**
     * Sends a request for authorization code with newly generates code verifier and challenge.
     * Code verifier and challenge are only saved on successful operation.
     *
     * @throws Client credentials are not present
     * @throws Failure to open in-app browser/redirect to springboard login page
     */
    async requestAuthorizationCode() {
      const codeVerifier = generateCodeVerifier()
      const codeChallenge = generateCodeChallenge(codeVerifier)

      // This should not happen as we make sure to check it during app start, but just a precaution
      if (!this.clientCredentials) {
        throw new Error("Client credentials during request for authorization code are undefined")
      }

      try {
        await requestAuthorizationCode(codeChallenge, this.clientCredentials.client_id)

        // Request successfully send, save variables
        this.codeCheck.codeVerifier = codeVerifier
        this.codeCheck.codeChallenge = codeChallenge
      } catch (error) {
        Log.debug("There was an error when requesting the authorization code.")
        throw error
      }
    },

    /**
     * Attempts to save the received authorization code to the store.
     * If they are not valid, it logs an error, but it doesn't throw it.
     *
     * @param authorizationCode
     */
    async handleAuthorizationCode(authorizationCode?: string | null) {
      if (authorizationCode) {
        this.authorizationCode = authorizationCode
      } else {
        Log.error({
          message: "Bad input for authorization code given",
          data: { authorizationCode },
        })
      }
    },

    /**
     * Sends a request for getting access token data.
     *
     * @throws Request payload is not valid
     * @throws Failed HTTP request
     */
    async getAccessToken() {
      // Only ask for access token if we have all available resources, else throw an error
      if (this.canRequestAccessToken) {
        try {
          // We know that those are not null due to canRequestAccessToken, but ESLint doesn't

          /* eslint-disable @typescript-eslint/no-non-null-assertion */

          this.authenticationToken = await requestAccessToken(
            this.authorizationCode!,
            this.clientCredentials!.client_id,
            this.codeCheck.codeVerifier!
          )

          /*eslint-enable @typescript-eslint/no-non-null-assertion */
        } catch (error) {
          if (error && typeof error === "object") {
            const errorObject = error as Record<string, unknown>
            if (errorObject.error === "invalid_grant") {
              // The authorization code expired, so we have to renew it
              this.authorizationCode = null
            }
          }

          Log.debug("An error occurred while trying to get the access token.")
          throw error
        }
      } else {
        await authenticationErrorDialog()

        // Leave a breadcrumb with the faulty data
        Log.debug({
          data: {
            authorizationCode: this.authorizationCode,
            clientId: this.clientCredentials?.client_id,
            codeVerifier: this.codeCheck?.codeVerifier,
          },
        })

        this.$reset()
        await router.push({ name: NamedPath.INITIATE_APP })

        throw new Error("Auth store has bad values for requesting access token")
      }
    },

    /**
     * Sends a request for verifying the access token.
     *
     */
    async verifyAccessToken() {
      try {
        if (!this.authenticationToken) {
          Log.error({
            message: "Auth store has bad values for access token.",
          })
          return
        }
        await verifyAccessToken(this.authenticationToken.access_token)
      } catch (error) {
        Log.debug({
          message: "An error occurred while trying to verify the access token.",
          data: { error },
        })
      }
    },

    /**
     * Sends a request for getting access token data from refresh token.
     *
     * @throws Request payload is not valid
     * @throws Failed HTTP request
     */
    async refreshAccessToken() {
      try {
        if (!this.clientCredentials || !this.authenticationToken) {
          await authenticationErrorDialog()
          Log.error({
            message: "Auth store has bad values for client credentials and authentication token.",
          })

          return
        }
        this.authenticationToken = await refreshAccessToken(
          this.clientCredentials.client_id,
          this.authenticationToken.refresh_token
        )
      } catch (error) {
        if (error && typeof error === "object") {
          const errorObject = error as Record<string, unknown>
          if (errorObject.error === "invalid_grant" || errorObject.error === "invalid_client") {
            // Cannot refresh token, so either revoked or someone screwed up
            this.authorizationCode = null
            this.authenticationToken = null
            this.clientCredentials = null
          }
        }
        Log.debug("An error occurred while trying to refresh the access token.")
        throw error
      }
    },

    /**
     * Sends a request for revoking the token.
     */
    async revokeToken() {
      try {
        if (!this.clientCredentials || !this.authenticationToken) {
          Log.error({
            message: "Auth store has bad values for client credentials and authentication token.",
          })
          return
        }
        await revokeToken(this.clientCredentials.client_id, this.authenticationToken.refresh_token)
      } catch (error) {
        Log.debug({
          message: "An error occurred while trying to revoke the token.",
          data: { error },
        })
      }
    },
  },
})
